import { useTranslation } from "react-i18next";
import { NewsDay } from "types/Post";

/** The UI language ('lt' or 'en'); a component that formats dates takes it from here so a language switch re-renders it. */
export const useUiLanguage = (): string => {
  const { i18n } = useTranslation();
  // The local i18next typing (src/i18next.d.ts) leaves out the runtime API, hence the cast.
  return (i18n as unknown as { language: string }).language;
};

/** Locale for Intl by the UI language (the app speaks Lithuanian and English). */
const intlLocale = (language: string) => (language === "lt" ? "lt-LT" : "en-US");

/** A server day ('YYYY-MM-DD') as a local date at noon, so no timezone moves it to another day. */
const dayToDate = (day: NewsDay): Date => {
  const [year, month, date] = day.split("-").map(Number);
  return new Date(year, month - 1, date, 12);
};

/** "spalio 18 d." */
export const formatMonthDay = (day: NewsDay, language: string): string =>
  new Intl.DateTimeFormat(intlLocale(language), { month: "long", day: "numeric" }).format(dayToDate(day));

/** "spalio 18 d." for a birthday, which has no year; 2000 is a leap year, so 29 February stays 29 February. */
export const formatBirthday = (month: number, day: number, language: string): string =>
  formatMonthDay(`2000-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`, language);

/** The twelve month names as they read on their own: "sausis" … "gruodis". */
export const listMonthNames = (language: string): string[] =>
  Array.from({ length: 12 }, (_, index) =>
    new Intl.DateTimeFormat(intlLocale(language), { month: "long" }).format(new Date(2000, index, 1, 12))
  );

/** "Pirmadienis, spalio 12 d." */
export const formatWeekdayAndDate = (date: Date, language: string): string => {
  const locale = intlLocale(language);
  const weekday = new Intl.DateTimeFormat(locale, { weekday: "long" }).format(date);
  const capitalizedWeekday = weekday.charAt(0).toLocaleUpperCase(locale) + weekday.slice(1);
  return `${capitalizedWeekday}, ${new Intl.DateTimeFormat(locale, { month: "long", day: "numeric" }).format(date)}`;
};

/** The server's day as a date, or the browser's own day while the server has not answered. */
export const dayOrNow = (day: NewsDay | undefined): Date => (day ? dayToDate(day) : new Date());

// Events carry a moment in time (epoch ms) and show it in the browser's own time zone,
// the same one the composer reads the date and time fields in.

/** "14:30" */
export const formatTimeOfDay = (startsAt: number): string => {
  const date = new Date(startsAt);
  return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
};

// Intl has no short month names for Lithuanian (it answers "10"), so the standard abbreviations are listed here.
const LITHUANIAN_SHORT_MONTHS = ["saus.", "vas.", "kov.", "bal.", "geg.", "birž.", "liep.", "rugp.", "rugs.", "spal.", "lapkr.", "gruod."];

/** "spal." / "Oct": the month on the date tile of an event. */
export const formatShortMonth = (startsAt: number, language: string): string => {
  const date = new Date(startsAt);
  if (language === "lt") return LITHUANIAN_SHORT_MONTHS[date.getMonth()];
  return new Intl.DateTimeFormat(intlLocale(language), { month: "short" }).format(date);
};

/** "2026-10-12 09:30": a moment in the browser's time zone. */
export const formatDateAndTime = (moment: number, language: string): string =>
  new Intl.DateTimeFormat(intlLocale(language), { dateStyle: "short", timeStyle: "short" }).format(new Date(moment));

/** "sekmadienis 18:00" */
export const formatWeekdayAndTime = (startsAt: number, language: string): string =>
  `${new Intl.DateTimeFormat(intlLocale(language), { weekday: "long" }).format(new Date(startsAt))} ${formatTimeOfDay(startsAt)}`;
